/**
 * Zero-asset sound engine.
 *
 * Every cue is synthesised with the Web Audio API so there are no audio
 * downloads, no licensing concerns, and no render-blocking requests. The
 * AudioContext is created lazily on the first user gesture to satisfy
 * browser autoplay policies.
 */
export type SoundName =
  | 'click'
  | 'hover'
  | 'bet'
  | 'deal'
  | 'flip'
  | 'chip'
  | 'spin'
  | 'reelStop'
  | 'win'
  | 'bigWin'
  | 'jackpot'
  | 'lose'
  | 'cashout'
  | 'tick'
  | 'reveal';

interface Tone {
  freq: number;
  duration: number;
  type: OscillatorType;
  gain: number;
  delay?: number;
  sweepTo?: number;
}

const CUES: Record<SoundName, Tone[]> = {
  click: [{ freq: 420, duration: 0.06, type: 'triangle', gain: 0.16 }],
  hover: [{ freq: 660, duration: 0.04, type: 'sine', gain: 0.06 }],
  bet: [
    { freq: 320, duration: 0.08, type: 'square', gain: 0.1 },
    { freq: 480, duration: 0.08, type: 'square', gain: 0.08, delay: 0.05 },
  ],
  deal: [{ freq: 260, duration: 0.09, type: 'sawtooth', gain: 0.09, sweepTo: 140 }],
  flip: [{ freq: 520, duration: 0.07, type: 'triangle', gain: 0.12, sweepTo: 880 }],
  chip: [
    { freq: 900, duration: 0.05, type: 'triangle', gain: 0.12 },
    { freq: 1200, duration: 0.06, type: 'triangle', gain: 0.08, delay: 0.03 },
  ],
  spin: [{ freq: 180, duration: 0.9, type: 'sawtooth', gain: 0.05, sweepTo: 90 }],
  reelStop: [{ freq: 340, duration: 0.07, type: 'square', gain: 0.11, sweepTo: 200 }],
  win: [
    { freq: 523.25, duration: 0.12, type: 'triangle', gain: 0.16 },
    { freq: 659.25, duration: 0.12, type: 'triangle', gain: 0.15, delay: 0.1 },
    { freq: 783.99, duration: 0.2, type: 'triangle', gain: 0.14, delay: 0.2 },
  ],
  bigWin: [
    { freq: 523.25, duration: 0.14, type: 'triangle', gain: 0.17 },
    { freq: 659.25, duration: 0.14, type: 'triangle', gain: 0.17, delay: 0.12 },
    { freq: 783.99, duration: 0.14, type: 'triangle', gain: 0.17, delay: 0.24 },
    { freq: 1046.5, duration: 0.4, type: 'triangle', gain: 0.16, delay: 0.36 },
  ],
  jackpot: [
    { freq: 440, duration: 0.12, type: 'square', gain: 0.14 },
    { freq: 554.37, duration: 0.12, type: 'square', gain: 0.14, delay: 0.1 },
    { freq: 659.25, duration: 0.12, type: 'square', gain: 0.14, delay: 0.2 },
    { freq: 880, duration: 0.5, type: 'square', gain: 0.14, delay: 0.3 },
  ],
  lose: [{ freq: 200, duration: 0.28, type: 'sine', gain: 0.1, sweepTo: 90 }],
  cashout: [
    { freq: 700, duration: 0.09, type: 'triangle', gain: 0.14 },
    { freq: 1050, duration: 0.16, type: 'triangle', gain: 0.12, delay: 0.08 },
  ],
  tick: [{ freq: 1100, duration: 0.025, type: 'square', gain: 0.05 }],
  reveal: [{ freq: 300, duration: 0.12, type: 'triangle', gain: 0.12, sweepTo: 1000 }],
};

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private enabled = true;
  private volume = 0.7;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setVolume(volume: number): void {
    this.volume = Math.min(Math.max(volume, 0), 1);
    if (this.master) this.master.gain.value = this.volume;
  }

  /** Must be called from a user-gesture handler at least once. */
  unlock(): void {
    this.ensureContext();
    void this.ctx?.resume();
  }

  play(name: SoundName): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || ctx.state === 'suspended') return;
    const now = ctx.currentTime;
    for (const tone of CUES[name]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = now + (tone.delay ?? 0);
      const end = start + tone.duration;

      osc.type = tone.type;
      osc.frequency.setValueAtTime(tone.freq, start);
      if (tone.sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(tone.sweepTo, 1), end);

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(tone.gain, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      osc.connect(gain).connect(this.master!);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (typeof window === 'undefined') return null;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }
}

export const sound = new SoundEngine();
